import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TextField, Button, Select, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@warmancer/ui';
import { accountsApi } from '../api/client';
import { formatDate } from '../utils/formatTimestamp';
import styles from './Accounts.module.css';

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'banned', label: 'Banned' },
  { value: 'suspended', label: 'Suspended' },
];

export function Accounts() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const limit = 20;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['accounts', { search, status, limit, offset: page * limit }],
    queryFn: () =>
      accountsApi.list({
        search: search || undefined,
        status: status || undefined,
        limit,
        offset: page * limit,
      }),
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    refetch();
  };

  const totalPages = Math.ceil((data?.pagination.total || 0) / limit);

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Accounts</h1>

      <form onSubmit={handleSearch} className={styles.filters}>
        <div className={styles.searchGroup}>
          <TextField
            name="search"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button type="submit">Search</Button>
        </div>

        <Select
          name="status"
          options={statusOptions}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(0);
          }}
        />
      </form>

      <div className={styles.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Fangs</TableHeader>
              <TableHeader>Verified</TableHeader>
              <TableHeader>Created</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.accounts.map((account) => (
              <TableRow key={account.id}>
                <TableCell>
                  <Link to={`/accounts/${account.id}`} className={styles.accountLink}>
                    {account.username_display}
                  </Link>
                </TableCell>
                <TableCell>{account.email}</TableCell>
                <TableCell>
                  <span className={`${styles.status} ${styles[`status${account.status}`]}`}>
                    {account.status}
                  </span>
                </TableCell>
                <TableCell>{account.fangs_balance.toLocaleString()}</TableCell>
                <TableCell>
                  {account.email_verified ? (
                    <span className={styles.verified}>Yes</span>
                  ) : (
                    <span className={styles.unverified}>No</span>
                  )}
                </TableCell>
                <TableCell>
                  {formatDate(account.created_at)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {isLoading && <div className={styles.loading}>Loading...</div>}
        {!isLoading && !data?.accounts.length && (
          <div className={styles.empty}>No accounts found</div>
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            className={styles.pageBtn}
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
